/**
 * Persistent multi-day cart for the PARENT family-ordering surface
 * (/orders/family). Quantities entered across any number of days + mealtimes +
 * children accumulate here and are submitted together from the right-rail cart.
 *
 * SERVER-SYNCED: the source of truth is the per-(household account, campus)
 * DRAFT cart behind `/api/family-cart`, so the cart follows the account
 * across devices and co-guardians instead of living in one browser tab
 * (which is also why there's no `persist` middleware here anymore — the
 * old sessionStorage cart is migrated to the server once, then removed).
 * Mutations apply optimistically to `linesByLocation`, record the touched
 * lines in `_pendingByKey`, and flush debounced ABSOLUTE upserts; server
 * hydration replaces local state except those dirty keys (see
 * `@/lib/family-orders/cart-sync` for the pure reconciliation model).
 * Lines are bucketed by `locationId` so a family ordering at more than one
 * campus keeps its carts isolated. The pure cart model (keys, upsert,
 * grouping, totals) lives in `@/lib/family-orders/cart` so it's
 * unit-testable without this store. Mirrors `@/stores/school-cart` —
 * which is still sessionStorage-local.
 */

import { toast } from '@ordo/ui/sonner'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { create } from 'zustand'

import {
  cartLineToWire,
  familyCartQueryKey,
  fetchFamilyCart,
  pushFamilyCartLines,
  wireToCartLine,
} from '@/api/family-cart'
import { ApiError } from '@/api/http'
import {
  buildCheckoutPlan,
  type CheckoutGroup,
  type FamilyCartLine,
  groupKey,
  subtotalCents,
  totalQty,
  upsertLine,
} from '@/lib/family-orders/cart'
import {
  hasPendingLines,
  linesByLocationEqual,
  mergeServerLines,
  type PendingByKey,
  planLegacyMigration,
  readLegacySessionCart,
  settleFlushed,
} from '@/lib/family-orders/cart-sync'
import { queryClient } from '@/lib/query-client'

import { useAuthStore } from './auth'

/** sessionStorage key the pre-sync builds persisted the cart under. */
const LEGACY_SESSION_KEY = 'ordo.family-cart'

const FLUSH_DEBOUNCE_MS = 500
const FLUSH_RETRY_MS = 15_000

interface FamilyCartState {
  /** Cart lines bucketed by service-location id. */
  linesByLocation: Record<string, FamilyCartLine[]>
  /**
   * Locally-edited lines awaiting a server flush (`quantity <= 0` =
   * delete tombstone). Internal to the sync engine — components never
   * read this; tests may.
   */
  _pendingByKey: PendingByKey
  /** Insert / update / remove a line (quantity <= 0 removes it). */
  setLine: (line: FamilyCartLine) => void
  /**
   * Merge `lines` into the cart, summing quantities for matching line keys —
   * the "Add meals to cart" commit. Lets the parent enter, add, clear the
   * inputs, and add more, accumulating into the same cart.
   */
  addLines: (locationId: string, lines: FamilyCartLine[]) => void
  /** Drop every line for a location (e.g. "Clear cart"). */
  clearLocation: (locationId: string) => void
  /**
   * Drop the lines belonging to a set of `${date}::${mealtime}` groups —
   * post-submit cleanup so successfully-submitted groups leave the cart while
   * any that failed stay for a retry. (The server also drains the matching
   * DRAFT lines on a successful submit; the tombstones flushed from here are
   * belt-and-braces — both are idempotent archives.)
   */
  clearGroups: (locationId: string, groupKeys: ReadonlySet<string>) => void
  /**
   * Reprice-at-submit (409 `price_changed`): overwrite `unitPriceCents`
   * on every line whose menuItemId appears in `priceByMenuItemId`, so
   * the cart UI matches the fresh server resolve whether the parent
   * confirms the resubmit or goes back to the cart. Flushes to the
   * server so other devices hydrate the patched prices too.
   */
  applyPriceChanges: (locationId: string, priceByMenuItemId: ReadonlyMap<string, number>) => void
}

export const useFamilyCart = create<FamilyCartState>()((set, get) => ({
  linesByLocation: {},
  _pendingByKey: {},
  setLine: (line) => {
    const current = get().linesByLocation[line.locationId] ?? []
    const next = upsertLine(current, line)
    set({
      linesByLocation: { ...get().linesByLocation, [line.locationId]: next },
      _pendingByKey: { ...get()._pendingByKey, [line.key]: { ...line } },
    })
    scheduleFamilyCartFlush()
  },
  addLines: (locationId, lines) => {
    const current = get().linesByLocation[locationId] ?? []
    const byKey = new Map(current.map((l) => [l.key, l]))
    const pending = { ...get()._pendingByKey }
    let touched = false
    for (const line of lines) {
      if (line.quantity <= 0) {
        continue
      }
      const existing = byKey.get(line.key)
      const merged = existing ? { ...existing, quantity: existing.quantity + line.quantity } : line
      byKey.set(line.key, merged)
      pending[line.key] = { ...merged }
      touched = true
    }
    set({ linesByLocation: { ...get().linesByLocation, [locationId]: [...byKey.values()] }, _pendingByKey: pending })
    if (touched) {
      scheduleFamilyCartFlush()
    }
  },
  clearLocation: (locationId) => {
    const next = { ...get().linesByLocation }
    const dropped = next[locationId] ?? []
    delete next[locationId]
    const pending = { ...get()._pendingByKey }
    for (const line of dropped) {
      pending[line.key] = { ...line, quantity: 0 }
    }
    set({ linesByLocation: next, _pendingByKey: pending })
    if (dropped.length > 0) {
      scheduleFamilyCartFlush()
    }
  },
  clearGroups: (locationId, groupKeys) => {
    const current = get().linesByLocation[locationId] ?? []
    const kept = current.filter((l) => !groupKeys.has(groupKey(l.date, l.mealtime)))
    const dropped = current.filter((l) => groupKeys.has(groupKey(l.date, l.mealtime)))
    const pending = { ...get()._pendingByKey }
    for (const line of dropped) {
      pending[line.key] = { ...line, quantity: 0 }
    }
    set({ linesByLocation: { ...get().linesByLocation, [locationId]: kept }, _pendingByKey: pending })
    if (dropped.length > 0) {
      scheduleFamilyCartFlush()
    }
  },
  applyPriceChanges: (locationId, priceByMenuItemId) => {
    const current = get().linesByLocation[locationId] ?? []
    const pending = { ...get()._pendingByKey }
    let touched = false
    const next = current.map((l) => {
      const newCents = priceByMenuItemId.get(l.menuItemId)
      if (newCents === undefined || l.unitPriceCents === newCents) {
        return l
      }
      const patched = { ...l, unitPriceCents: newCents }
      pending[patched.key] = { ...patched }
      touched = true
      return patched
    })
    set({ linesByLocation: { ...get().linesByLocation, [locationId]: next }, _pendingByKey: pending })
    if (touched) {
      scheduleFamilyCartFlush()
    }
  },
}))

// ---------------------------------------------------------------------------
// Sync engine — hydration, debounced flush, auth reset. Module-scope so the
// store's mutations stay synchronous; all timers are best-effort and every
// wire write is an idempotent absolute upsert (safe to repeat).
// ---------------------------------------------------------------------------

let flushTimer: ReturnType<typeof setTimeout> | null = null
let retryTimer: ReturnType<typeof setTimeout> | null = null
let flushInFlight = false
let legacyMigrationDone = false

function clearTimers(): void {
  if (flushTimer != null) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
  if (retryTimer != null) {
    clearTimeout(retryTimer)
    retryTimer = null
  }
}

/** Debounced flush — coalesces a burst of stepper taps into one request. */
export function scheduleFamilyCartFlush(delayMs: number = FLUSH_DEBOUNCE_MS): void {
  if (typeof window === 'undefined') {
    return
  }
  if (flushTimer != null) {
    clearTimeout(flushTimer)
  }
  flushTimer = setTimeout(() => {
    flushTimer = null
    void flushFamilyCartNow()
  }, delayMs)
}

/**
 * Push every pending line as an absolute upsert. Single-flight; on
 * success settles the flushed keys (edits that landed mid-flight stay
 * pending) and re-hydrates from the authoritative response; on failure
 * keeps the queue and retries on the next mutation / focus / timer.
 */
export async function flushFamilyCartNow(): Promise<void> {
  if (flushInFlight) {
    return
  }
  const snapshot = { ...useFamilyCart.getState()._pendingByKey }
  const keys = Object.keys(snapshot)
  if (keys.length === 0) {
    return
  }
  flushInFlight = true
  try {
    const response = await pushFamilyCartLines(keys.map((key) => cartLineToWire(snapshot[key] as FamilyCartLine)))
    let pending = settleFlushed(useFamilyCart.getState()._pendingByKey, snapshot)
    if (response.rejected.length > 0) {
      // The server refused these lines (stale member, foreign household,
      // bad reference) — drop them so they can't wedge the queue; the
      // hydrate below also removes them from the visible cart.
      pending = { ...pending }
      for (const rejection of response.rejected) {
        const key = keys[rejection.index]
        if (key) {
          delete pending[key]
        }
      }
      toast.error("Some items couldn't be kept in your cart", {
        description: response.rejected[0]?.message,
      })
    }
    useFamilyCart.setState({ _pendingByKey: pending })
    queryClient.setQueryData(familyCartQueryKey, { lines: response.lines })
    applyServerLines(response.lines.map(wireToCartLine))
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      // Session is gone — nothing these lines could ever flush into.
      useFamilyCart.setState({ _pendingByKey: {} })
    } else if (typeof window !== 'undefined') {
      if (retryTimer != null) {
        clearTimeout(retryTimer)
      }
      retryTimer = setTimeout(() => {
        retryTimer = null
        void flushFamilyCartNow()
      }, FLUSH_RETRY_MS)
    }
  } finally {
    flushInFlight = false
    if (flushTimer == null && retryTimer == null && hasPendingLines(useFamilyCart.getState()._pendingByKey)) {
      // Edits landed while the request was in flight — flush them next.
      scheduleFamilyCartFlush()
    }
  }
}

/** Merge authoritative server lines into the store (pending edits win). */
function applyServerLines(serverLines: FamilyCartLine[]): void {
  const state = useFamilyCart.getState()
  const merged = mergeServerLines(serverLines, state._pendingByKey)
  if (!linesByLocationEqual(merged, state.linesByLocation)) {
    useFamilyCart.setState({ linesByLocation: merged })
  }
}

/**
 * Hydrate from a GET response. The first hydration per page load also
 * runs the one-time sessionStorage migration: legacy lines whose key the
 * server doesn't know yet are queued as ordinary pending edits
 * (server-wins — see `planLegacyMigration`), then the legacy key is
 * removed for good.
 */
export function hydrateFamilyCartFromServer(serverLines: FamilyCartLine[]): void {
  if (!legacyMigrationDone) {
    legacyMigrationDone = true
    try {
      const legacy = readLegacySessionCart(window.sessionStorage.getItem(LEGACY_SESSION_KEY))
      const migrate = planLegacyMigration(legacy, serverLines, useFamilyCart.getState()._pendingByKey)
      if (migrate.length > 0) {
        const pending = { ...useFamilyCart.getState()._pendingByKey }
        for (const line of migrate) {
          pending[line.key] = line
        }
        useFamilyCart.setState({ _pendingByKey: pending })
        scheduleFamilyCartFlush()
      }
      window.sessionStorage.removeItem(LEGACY_SESSION_KEY)
    } catch {
      // sessionStorage unavailable (privacy mode) — nothing to migrate.
    }
  }
  applyServerLines(serverLines)
}

/** Wipe local cart state (auth changed / signed out). */
export function resetFamilyCart(): void {
  clearTimers()
  useFamilyCart.setState({ linesByLocation: {}, _pendingByKey: {} })
  try {
    // A pre-sync cart left by a previous user on this tab must never
    // migrate into the next user's account.
    window.sessionStorage.removeItem(LEGACY_SESSION_KEY)
  } catch {
    // sessionStorage unavailable — nothing to clean.
  }
}

/**
 * Server-cart hydration hook — mount once per family ordering surface
 * (week view, day view, checkout). TanStack dedupes the query by key, so
 * multiple mounts share one fetch; `refetchOnWindowFocus` is what makes
 * picking up the phone show the cart the desktop just built.
 */
export function useFamilyCartSync(): { isPending: boolean } {
  const user = useAuthStore((s) => s.user)
  const query = useQuery({
    queryKey: familyCartQueryKey,
    queryFn: ({ signal }) => fetchFamilyCart(signal),
    enabled: user != null,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  })
  const lines = query.data?.lines
  useEffect(() => {
    if (lines) {
      hydrateFamilyCartFromServer(lines.map(wireToCartLine))
    }
  }, [lines])
  return { isPending: user != null && query.isPending }
}

if (typeof window !== 'undefined') {
  // Flush leftovers when the tab regains focus (complements the query's
  // refetchOnWindowFocus) and, best-effort, when the page is going away —
  // sendBeacon survives tab close where fetch would be cancelled. Both are
  // idempotent absolute upserts, so double-delivery is harmless.
  window.addEventListener('focus', () => {
    if (hasPendingLines(useFamilyCart.getState()._pendingByKey)) {
      void flushFamilyCartNow()
    }
  })
  window.addEventListener('pagehide', () => {
    const pending = useFamilyCart.getState()._pendingByKey
    const keys = Object.keys(pending)
    if (keys.length === 0) {
      return
    }
    try {
      const body = JSON.stringify({ lines: keys.map((key) => cartLineToWire(pending[key] as FamilyCartLine)) })
      navigator.sendBeacon('/api/family-cart/lines', new Blob([body], { type: 'application/json' }))
    } catch {
      // Best-effort only — the lines stay pending and re-flush wherever
      // this account loads the cart next.
    }
  })

  // Auth transitions: wipe the local cart on logout/user switch (fixes the
  // old "cart survives logout on a shared tab" leak) and refetch on login.
  let lastAuthUserId: string | null = useAuthStore.getState().user?.id ?? null
  useAuthStore.subscribe((state) => {
    const nextUserId = state.user?.id ?? null
    if (nextUserId === lastAuthUserId) {
      return
    }
    lastAuthUserId = nextUserId
    resetFamilyCart()
    if (nextUserId) {
      void queryClient.invalidateQueries({ queryKey: familyCartQueryKey })
    } else {
      queryClient.removeQueries({ queryKey: familyCartQueryKey })
    }
  })
}

// ---------------------------------------------------------------------------
// Selectors — call with `useFamilyCart(selectLinesFor(locationId))` so the
// component re-renders only when that location's slice changes.
// ---------------------------------------------------------------------------

const EMPTY: FamilyCartLine[] = []

export const selectLinesFor = (locationId: string) => (s: FamilyCartState) => s.linesByLocation[locationId] ?? EMPTY

/**
 * Stable reference to the per-location buckets — flatten with
 * `useAllCartLines()` (below) or `allLines()` when a surface aggregates
 * every campus (the family views show all children at once).
 */
export const selectLinesByLocation = (s: FamilyCartState) => s.linesByLocation

export function linesFor(state: FamilyCartState, locationId: string): FamilyCartLine[] {
  return state.linesByLocation[locationId] ?? EMPTY
}

/** Every cart line across all campuses (plain helper — not a selector). */
export function allLines(state: FamilyCartState): FamilyCartLine[] {
  return Object.values(state.linesByLocation).flat()
}

/** Hook: every cart line across all campuses, memoised per store update. */
export function useAllCartLines(): FamilyCartLine[] {
  const buckets = useFamilyCart(selectLinesByLocation)
  return useMemo(() => Object.values(buckets).flat(), [buckets])
}

export function cartTotalQty(state: FamilyCartState, locationId: string): number {
  return totalQty(linesFor(state, locationId))
}

export function cartSubtotalCents(state: FamilyCartState, locationId: string): number | null {
  return subtotalCents(linesFor(state, locationId))
}

export function cartGroupsFor(state: FamilyCartState, locationId: string): CheckoutGroup[] {
  return buildCheckoutPlan(linesFor(state, locationId))
}
